// Invite links — "Create tracked link", from Internal → People (a row's menu, or the header)
// and from Analytics → Website. Makes `https://vera.inc/invite/<code>` and shows it to copy.
// Nothing is emailed: the link is for staff to send however they like.
import React, { useEffect, useMemo, useState } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { CheckIcon, CopyIcon } from 'lucide-react'
import { Button, FieldLabel, Input, Modal } from '@repo/ui'

import { SelectField } from 'components/input/fields'
import { useSnackbar } from 'contexts'
import { useCompaniesQuery } from 'hooks/api/useCompaniesQuery'
import { usePeople, type Person } from 'hooks/api/usePeople'
import { useTrackedLinkActions, type TrackedLink } from 'hooks/api/useTrackedLinks'
import { getErrorMessage } from 'utils'
import { companyPickerOptions, listedCompanies } from 'utils/listedCompany'

import { DESTINATION_OPTIONS, OTHER_PATH, destinationError } from './destinations'

const NONE = '__none'

const personName = (p: Pick<Person, 'name' | 'email'>) => p.name || p.email || 'Unnamed'

type State = {
  label: string
  personKey: string
  destination: string
  otherPath: string
  companyId: string
  companyName: string
  campaign: string
}

export const CreateTrackedLinkModal = ({
  open,
  onClose,
  person,
}: {
  open: boolean
  onClose: () => void
  /** Opened from a People row: the link is for this person (not changeable here). */
  person?: Person | null
}) => {
  const { open: notification } = useSnackbar()
  const { create } = useTrackedLinkActions()
  // The person picker is only needed when no row opened the dialog.
  const { data: people = [] } = usePeople()
  const { data: companies } = useCompaniesQuery({ limit: 500, sortField: 'name', sortDir: 'asc' }, { enabled: open })

  const initial = (): State => ({
    label: person ? `${personName(person)} invite` : '',
    personKey: person?.key ?? NONE,
    destination: '/',
    otherPath: '',
    companyId: person?.platform_company?.id ?? NONE,
    companyName: person?.platform_company ? '' : person?.company ?? '',
    campaign: '',
  })
  const [s, setS] = useState<State>(initial)
  const [errors, setErrors] = useState<Partial<Record<'label' | 'destination', string>>>({})
  const [made, setMade] = useState<TrackedLink | null>(null)
  const [copied, setCopied] = useState(false)
  const set = (patch: Partial<State>) => setS((prev) => ({ ...prev, ...patch }))

  useEffect(() => {
    if (open) {
      setS(initial())
      setErrors({})
      setMade(null)
      setCopied(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, person?.key])

  const personOptions = useMemo(
    () => [
      { value: NONE, label: 'No one in particular' },
      ...people.map((p) => ({ value: p.key, label: p.email && p.name ? `${p.name} · ${p.email}` : personName(p) })),
    ],
    [people],
  )
  const chosenPerson = person ?? people.find((p) => p.key === s.personKey) ?? null

  // VRG-706: a `/companies/admin` row is `{ company: {id, name}, … }`, not a company. Reading
  // `c.id` off the row gave one blank option per company, and picking one sent
  // `company_id: "undefined"` (400 "Unknown company"). The person's own company is added when
  // the list leaves it out, so a pre-filled value always has an option to show.
  const companyOptions = useMemo(
    () => [{ value: NONE, label: 'Not a platform company' }, ...companyPickerOptions(listedCompanies(companies), chosenPerson?.platform_company)],
    [companies, chosenPerson?.platform_company],
  )

  // `auto`: the copy attempted right after creation. Some browsers refuse a clipboard write
  // that is not directly inside a click, so a failure there stays quiet: the link and the
  // Copy button are on screen. A failed click on Copy says so.
  const copy = async (url: string, auto = false) => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      notification({ type: 'success', title: 'Link copied' })
    } catch {
      if (!auto) notification({ type: 'error', title: 'Could not copy. Select the link and copy it.' })
    }
  }

  const submit = (e?: React.FormEvent) => {
    e?.preventDefault()
    const destination = s.destination === OTHER_PATH ? s.otherPath.trim() : s.destination
    const next: typeof errors = {}
    if (!s.label.trim()) next.label = 'Give the link a label'
    const destErr = destinationError(destination)
    if (destErr) next.destination = destErr
    setErrors(next)
    if (Object.keys(next).length) return
    create.mutate(
      {
        label: s.label.trim(),
        destination,
        person_key: chosenPerson?.key,
        person_label: chosenPerson ? personName(chosenPerson) : undefined,
        company_id: s.companyId !== NONE ? s.companyId : undefined,
        company_name: s.companyId === NONE && s.companyName.trim() ? s.companyName.trim() : undefined,
        campaign: s.campaign.trim() || undefined,
      },
      {
        onSuccess: (link) => {
          setMade(link)
          void copy(link.url, true)
        },
        onError: (error) => notification({ type: 'error', title: getErrorMessage(error) }),
      },
    )
  }

  if (made) {
    return (
      <Modal
        title="Tracked link ready"
        open={open}
        onClose={onClose}
        description="Send it however you like. Clicks show up on Access and on Analytics → Website."
        actions={
          <>
            <Button variant="secondary" onClick={onClose}>
              Done
            </Button>
            <Button onClick={() => copy(made.url)} startIcon={copied ? <CheckIcon strokeWidth={1} /> : <CopyIcon strokeWidth={1} />}>
              {copied ? 'Copied' : 'Copy link'}
            </Button>
          </>
        }
      >
        <Stack spacing={3} mt={2}>
          <Box
            sx={{ p: 3, borderRadius: 1, border: 1, borderColor: 'divider', fontFamily: 'monospace', fontSize: 14, wordBreak: 'break-all', userSelect: 'all' }}
            data-testid="tracked-link-url"
          >
            {made.url}
          </Box>
          <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            Opens {made.destination}
            {made.person_label ? ` · for ${made.person_label}` : ''}
            {made.campaign ? ` · campaign ${made.campaign}` : ''}. Anyone they forward it to counts as them.
          </Typography>
        </Stack>
      </Modal>
    )
  }

  return (
    <Modal
      title="Create tracked link"
      open={open}
      onClose={onClose}
      description={
        person
          ? `A vera.inc link for ${personName(person)}. You'll see when it's clicked and, for visitors whose analytics are on, what they look at.`
          : "A vera.inc link you'll see clicks for and, for visitors whose analytics are on, what they look at."
      }
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button onClick={() => submit()} loading={create.isPending}>
            Create link
          </Button>
        </>
      }
    >
      <Stack component="form" onSubmit={submit} spacing={4} mt={2}>
        <Input
          label="Label"
          labelAdornment="(staff only)"
          value={s.label}
          onValueChange={(v: string) => set({ label: v })}
          placeholder="Art fair follow-up"
          error={Boolean(errors.label)}
          helperText={errors.label}
        />

        {!person && (
          <Stack>
            <FieldLabel label="For" />
            <SelectField
              options={personOptions}
              value={s.personKey}
              search
              onChange={(v) => {
                const key = String(v)
                const p = people.find((x) => x.key === key)
                set({
                  personKey: key,
                  ...(p && !s.label.trim() ? { label: `${personName(p)} invite` } : {}),
                  ...(p?.platform_company ? { companyId: p.platform_company.id, companyName: '' } : {}),
                  ...(p && !p.platform_company && p.company ? { companyName: p.company } : {}),
                })
              }}
            />
          </Stack>
        )}

        <Stack spacing={2}>
          <FieldLabel label="Opens" />
          <SelectField options={DESTINATION_OPTIONS} value={s.destination} search onChange={(v) => set({ destination: String(v) })} />
          {s.destination === OTHER_PATH && (
            <Input value={s.otherPath} onValueChange={(v: string) => set({ otherPath: v })} placeholder="/industries/art" />
          )}
          {errors.destination && (
            <Typography variant="body4" color="error">
              {errors.destination}
            </Typography>
          )}
        </Stack>

        <Stack spacing={2}>
          <FieldLabel label="Company" adornment="(optional)" />
          <SelectField options={companyOptions} value={s.companyId} search onChange={(v) => set({ companyId: String(v) })} />
          {s.companyId === NONE && (
            <Input value={s.companyName} onValueChange={(v: string) => set({ companyName: v })} placeholder="Company name, if not on the platform" />
          )}
          <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            The link&apos;s code starts with the company (or the label), never the person&apos;s name. Recipients can see it.
          </Typography>
        </Stack>

        <Input
          label="Campaign"
          labelAdornment="(optional)"
          value={s.campaign}
          onValueChange={(v: string) => set({ campaign: v })}
          placeholder="art-fair-2026"
        />
      </Stack>
    </Modal>
  )
}
